import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'

import BookkeepingManagerDetailsViewModel from '../BookkeepingManagerDetailsViewModel'
import { timeago } from 'utils/date-util'
import BookkeepingPendingAction, {
  BookkeepingManagerAction,
} from 'admin/bookkeeping/BookkeepingPendingAction'
import { match } from 'utils/pattern-match-util'

import styles from './DetailsContent.sass'
import JobScopeChangeRequest from 'jobs/JobScopeChange'
import { JobScopeChangeStatus } from 'jobs/dtos/job-scope-change'

interface DetailsContentProps {
  vm: BookkeepingManagerDetailsViewModel
}

export default observer(function DetailsContent({ vm }: DetailsContentProps) {
  return (
    <div className={styles.root}>
      <h2 className={styles.title}>{vm.bookkeepingPendingAction.jobName}</h2>
      <div className={styles.content}>
        <ContentItem label="Job ID">
          {vm.bookkeepingPendingAction.jobShortId}
        </ContentItem>
        <ContentItem label="Company ID">
          {vm.bookkeepingPendingAction.platformConnection?.displayId ?? 'N/A'}
        </ContentItem>
        <ContentItem label="Company Name">
          {vm.bookkeepingPendingAction.platformConnection?.displayName ?? 'N/A'}
        </ContentItem>
        <ContentItem label="Method">
          {vm.bookkeepingPendingAction.displayAccessMethod}
        </ContentItem>
        <ContentAction vm={vm.bookkeepingPendingAction} />
        <ContentItem label="Time in action">
          {timeago(vm.bookkeepingPendingAction.datePendingSince, 'long')}
        </ContentItem>
        <ContentInstructions action={vm.bookkeepingPendingAction} />
        {vm.latestScopeChange && (
          <ContentJobScopeChangeRequest vm={vm.latestScopeChange} />
        )}
      </div>
    </div>
  )
})

interface ContentActionProps {
  vm: BookkeepingPendingAction
}

function ContentAction({ vm }: ContentActionProps) {
  return (
    <>
      <div className={styles.label}>Action</div>
      <div
        className={cx(
          styles.value,
          styles[`action-${formatActionSingleWorld(vm.action)}`],
        )}
      >
        {vm.displayAction}
      </div>
    </>
  )
}

interface ContentJobScopeChangeRequestStatusProps {
  vm: JobScopeChangeRequest
}

function ContentJobScopeChangeRequest({
  vm,
}: ContentJobScopeChangeRequestStatusProps) {
  return (
    <>
      <div className={styles.label}>Latest scope change status</div>
      <div
        className={cx(
          styles.value,
          styles[
            `job-scope-change-status-${formatStatusSingleWord(vm.status)}`
          ],
        )}
      >
        {match(vm.status, {
          Accepted: () => 'Accepted',
          Rejected: () => 'Rejected',
          PendingFinalization: () => 'Pending finalization',
          PendingAdminApproval: () => 'Pending admin approval',
          PendingClientApproval: () => 'Pending client approval',
          ApplyFailed: () => 'Apply failed',
          Unspecified: () => 'Unspecified',
        })}
      </div>
    </>
  )
}

interface ContentItemProps {
  label: string
  children?: React.ReactNode
  danger?: boolean
}

function ContentItem({ label, danger, children }: ContentItemProps) {
  return (
    <>
      <div className={styles.label}>{label}</div>
      <div className={cx(styles.value, danger && styles.danger)}>
        {children}
      </div>
    </>
  )
}

interface InstructionsProps {
  action: BookkeepingPendingAction
}

function ContentInstructions({ action }: InstructionsProps) {
  const content = match(action.action, {
    [BookkeepingManagerAction.ConfirmAccess]: () => (
      <>
        <p>{action.platformConnection?.helpText}</p>
        <br />
        <p>This will allow the Pro to start the assessment.</p>
      </>
    ),
    [BookkeepingManagerAction.AssessmentReview]: () => (
      <>Please check the assessment and make any adjustments necessary.</>
    ),
    [BookkeepingManagerAction.Unspecified]: () => <>Unspecified</>,
  })

  return <ContentItem label="Instructions">{content}</ContentItem>
}

function formatStatusSingleWord(status: JobScopeChangeStatus) {
  return match(status, {
    Accepted: () => 'accepted',
    Rejected: () => 'rejected',
    ApplyFailed: () => 'rejected',
    PendingFinalization: () => 'pending',
    PendingAdminApproval: () => 'pending',
    PendingClientApproval: () => 'pending',
    Unspecified: () => '',
  })
}

function formatActionSingleWorld(action: BookkeepingManagerAction) {
  return match(action, {
    [BookkeepingManagerAction.ConfirmAccess]: () => 'confirm-access',
    [BookkeepingManagerAction.AssessmentReview]: () => 'assessment-review',
    [BookkeepingManagerAction.Unspecified]: () => '',
  })
}
